<template>
  <div class="plot-page">
    <plot-list :novelId="novelId" :plotId="effectivePlotId" :subPlots="subPlots" />
    <plots
      v-if="plot"
      class="plot"
      :novel-id="novelId"
      :plotId="effectivePlotId"
      :plot="plot"
      @showHistoryDialog="isHistoryManageDialogOpen = true"
    />
    <history-manage-dialog
      v-if="plot"
      :is-open="isHistoryManageDialogOpen"
      data-type="pl"
      :title="historyDialogTitle"
      :novel-id="novelId"
      :data-id="plotDataId"
      @close="isHistoryManageDialogOpen = false"
      @restore="onPlotRestore"
    >
      <template v-slot:content="{ historyItem }">
        <div v-if="historyItem">
          <plots
            :novel-id="novelId"
            :plot-id="historyItem.item.plotId"
            :plot="historyItem.item"
            :is-viewed-by-external-user="true"
          />
        </div>
      </template>
    </history-manage-dialog>
    <slide-shared-link-menu
      v-if="isEnableMobile && !isHamburgerMenuOpen"
      class="slide-menu"
      :novelId="novelId"
      kind="plot"
    />
    <div v-if="isShowPublishSharedLink" class="dialog">
      <publish-shared-link class="dialog-content" :novelId="novelId" kind="plot" />
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import Plots from "@/components/organisms/Plots.vue";
import PlotList from "@/components/organisms/PlotList.vue";
import PublishSharedLink from "@/components/organisms/PublishSharedLink.vue";
import HistoryManageDialog from "@/components/ui/dialogs/HistoryManageDialog.vue";
import { Plot, SubPlot } from "@/lib/models";
import isMobile from "ismobilejs";
import SlideSharedLinkMenu from "@/components/organisms/SlideSharedLinkMenu.vue";
import { hasHistoryQuery, isOverallPlotRouteId, omitHistoryQuery } from "@/lib/utils/deepLink";

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { Plots, PlotList, PublishSharedLink, HistoryManageDialog, SlideSharedLinkMenu },
  props: {
    novelId: String,
    plotId: String,
  },
  data() {
    return {
      isHistoryManageDialogOpen: false,
    };
  },
  async created() {
    const { $store, novelId, $router, $route } = this;

    if ($route.path.includes("edit")) {
      // 全体プロットのプロット編集画面でリロードした際に、
      // 白紙の画面が表示される不具合を防ぐため、プロット編集画面に遷移する
      $router.push({ name: "plotEdit", params: { novelId } });
      return;
    }
    if ($route.path.includes("change")) {
      // 全体プロットのモード変更画面でリロードされた場合、ルート設定 (path: "/:novelId/plot/:plotId?") により
      // URL の 3 番目のパラメータが plotId として渡される
      // そのため、computed の plot を使用せず(サブプロットを取得してしまう為)、直接全体プロット (plots) を取得する
      const plots = $store.getters["plotModule/plots"];

      // 全体プロットのモード変更画面でリロードした際に、
      // 白紙の画面が表示される不具合を防ぐため、モード変更画面に遷移する
      $router.push({
        name: "plotChange",
        params: { novelId },
        query: { mode: plots.mode || "kishoutenketsu" },
      });
      return;
    }
    const { dispatch } = $store;

    await dispatch("plotModule/initialize", novelId);

    this.$store.dispatch("sharedLinkModule/initializeSharedLinkList", novelId);
    this.$store.dispatch("sharedLinkModule/initializeSharedLinkListBelongOwner");
    this.$store.dispatch("sharedLinkModule/initializeCommentListBelongOwner", novelId);
    this.openHistoryFromQuery();
  },
  computed: {
    effectivePlotId() {
      return isOverallPlotRouteId(this.plotId) ? undefined : this.plotId;
    },
    plot() {
      const { $store, effectivePlotId } = this;

      if (effectivePlotId) {
        return $store.getters["plotModule/subPlot"](effectivePlotId);
      }
      return $store.getters["plotModule/plots"];
    },
    subPlots() {
      const { $store } = this;
      return $store.getters["plotModule/subPlots"];
    },
    isShowPublishSharedLink() {
      return this.$store.getters["sharedLinkModule/isShowPublishSharedLink"];
    },
    isEnableMobile() {
      return !isMobile().phone && !(isMobile().tablet && window.innerWidth < 2048);
    },
    isHamburgerMenuOpen() {
      return this.$store.getters["plotModule/isHamburgerMenuOpen"];
    },
    plotDisplayName() {
      const { plot, effectivePlotId } = this;
      if (effectivePlotId) return (plot as SubPlot).name || "No Name";
      return (this.$t("plotPage.overallPlot") as string) || "No Name";
    },
    historyDialogTitle() {
      return `${this.$t("historyManageDialog.title.plotDetail")} - ${this.plotDisplayName}`;
    },
    plotDataId() {
      return this.effectivePlotId || "default";
    },
  },
  methods: {
    async onPlotRestore() {
      this.isHistoryManageDialogOpen = false;
      await this.$store.dispatch("plotModule/initialize", this.novelId);
    },
    openHistoryFromQuery() {
      if (!hasHistoryQuery(this.$route.query) || !this.plot) return;
      this.isHistoryManageDialogOpen = true;
      this.$router.replace({
        name: this.$route.name as string,
        params: this.$route.params,
        query: omitHistoryQuery(this.$route.query) as Record<string, string | (string | null)[]>,
      });
    },
  },
  watch: {
    $route: {
      handler() {
        // 同一コンポーネント内でルート変更時にスクロール位置をリセット
        this.$nextTick(() => {
          const plotListElement = document.querySelector(".plot-page .plots .plot-list");
          if (plotListElement) {
            plotListElement.scrollTop = 0;
            plotListElement.scrollLeft = 0;
          }
        });
      },
      immediate: true,
    },
  },
});

interface Props {
  novelId: string;
  plotId?: string;
}

interface Data {
  isHistoryManageDialogOpen: boolean;
}

interface Computed {
  effectivePlotId: string | undefined;
  plot: Plot | SubPlot;
  subPlots: SubPlot[];
  isShowPublishSharedLink: boolean;
  isEnableMobile: boolean;
  isHamburgerMenuOpen: boolean;
  plotDisplayName: string;
  historyDialogTitle: string;
  plotDataId: string;
}

interface Methods {
  onPlotRestore(): Promise<void>;
  openHistoryFromQuery(): void;
}
</script>

<style lang="scss" scoped>
.plot-page {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;

  .slide-menu {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    // margin-top: 60px;
  }
  .plot-list {
    height: 50px;
  }

  .plot {
    height: calc(100% - 60px);
    flex-grow: 1;
    z-index: 1;
  }

  .dialog {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--color-overlay);
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
